import dayjs, { Dayjs } from 'dayjs';
import { DateOptions } from 'store/api/enums';
import {
  ApiDateRange,
  getApiDateRangeForDateOption,
  getApiDateRangeFromDayjs,
  getDayjsRangeForDateOption,
} from 'utils/date-option-range';

export const DEFAULT_DRAWER_DATE_OPTION = DateOptions.THIS_MONTH;
export const FALLBACK_DRAWER_DATE_OPTION = DateOptions.THIS_YEAR;

/** Presets that only include future dates — hidden from file/history date filters. */
export const FUTURE_DATE_OPTIONS = new Set<DateOptions>([
  DateOptions.NEXT_WEEK,
  DateOptions.NEXT_MONTH,
  DateOptions.NEXT_QUARTER,
]);

export const DRAWER_DATE_FILTER_OPTIONS = Object.values(DateOptions).filter(
  (option) => !FUTURE_DATE_OPTIONS.has(option)
);

export const DRAWER_DATE_DISPLAY_FORMAT = 'DD/MM/YYYY';

export type DrawerDateRangeState = {
  dateRangeOption: DateOptions | undefined;
  enableCustomRange: boolean;
  customRange: [Dayjs | null, Dayjs | null];
  appliedRange: ApiDateRange;
};

export function getDrawerDateRangeStateForOption(
  option: DateOptions
): DrawerDateRangeState {
  if (option === DateOptions.ALL) {
    return {
      dateRangeOption: DateOptions.ALL,
      enableCustomRange: false,
      customRange: [null, null],
      appliedRange: {},
    };
  }

  const customRange =
    getDayjsRangeForDateOption(option) ?? ([null, null] as const);
  return {
    dateRangeOption: option,
    enableCustomRange: false,
    customRange: [...customRange],
    appliedRange: getApiDateRangeForDateOption(option),
  };
}

export function isUploadDateInRange(
  uploadDate: string | number | null | undefined,
  range: ApiDateRange
): boolean {
  if (!range.from && !range.to) return true;
  if (uploadDate == null || uploadDate === '') return false;

  const date = dayjs(uploadDate);
  if (!date.isValid()) return false;

  const from = range.from
    ? dayjs(range.from, 'YYYY-MM-DD').startOf('day')
    : null;
  const to = range.to ? dayjs(range.to, 'YYYY-MM-DD').endOf('day') : null;

  if (from?.isValid() && date.isBefore(from)) return false;
  if (to?.isValid() && date.isAfter(to)) return false;
  return true;
}

export function countItemsInDateRange<T extends { uploadDate?: unknown }>(
  items: T[],
  range: ApiDateRange
): number {
  return items.filter((item) =>
    isUploadDateInRange(
      item.uploadDate as string | number | null | undefined,
      range
    )
  ).length;
}

export function resolveDrawerDateRangeFromCustom(
  range: [Dayjs | null, Dayjs | null]
): ApiDateRange {
  if (range[0] && range[1]) {
    return getApiDateRangeFromDayjs(range);
  }
  return {};
}
